import React, { forwardRef } from 'react'
import PropTypes from 'prop-types'

/**
 * ShareableImage Component
 *
 * A reusable component for rendering content off-screen that can be
 * converted to an image. Use with the `useShareableImage` hook.
 *
 * @example
 * ```jsx
 * const MyComponent = () => {
 *   const shareableImage = useShareableImage({
 *     name: 'my-image',
 *     width: 1200,
 *     height: 630,
 *     generateOnLoad: true,
 *   })
 *
 *   return (
 *     <>
 *       <ShareableImage {...shareableImage}>
 *         <YourCustomContent />
 *       </ShareableImage>
 *       <Button onClick={shareableImage.downloadImage} disabled={shareableImage.generatingImage}>
 *         Download
 *       </Button>
 *     </>
 *   )
 * }
 * ```
 */
const ShareableImage = forwardRef(
  (
    { children, width = 1200, height = 'auto', style = {}, backgroundColor = 'white', className = '', hide = false },
    ref
  ) => {
    const containerStyle = {
      position: 'fixed',
      top: 0,
      left: 0,
      width,
      height,
      backgroundColor,
      overflow: 'hidden',
      pointerEvents: 'none',
      clipPath: 'inset(100%)',
      zIndex: -9999,
      ...style,
      ...(hide && { display: 'none' }),
    }

    return (
      <div
        ref={ref}
        style={containerStyle}
        className={className}
        // Off-screen source for the generated share image, not page content
        aria-hidden="true"
        id="shareable-image-container"
        data-testid="shareable-image"
      >
        {children}
      </div>
    )
  }
)

ShareableImage.displayName = 'ShareableImage'

ShareableImage.propTypes = {
  /** Content to be rendered and converted to an image */
  children: PropTypes.node.isRequired,
  /** Width of the output image in pixels */
  width: PropTypes.number,
  /** Height of the output image in pixels (optional, auto if not specified) */
  height: PropTypes.number,
  /** Background color of the image */
  backgroundColor: PropTypes.string,
  /** Additional inline styles */
  style: PropTypes.shape({}),
  /** Additional CSS class names */
  className: PropTypes.string,
  /** Hide the component after image generation */
  hide: PropTypes.bool,
}

export default ShareableImage
